import type { ConversationDetail } from '@chatbot/shared-types';
import { useEffect, useRef } from 'react';
import { EmptyState } from '../EmptyState.tsx';
import { Skeleton } from '../Skeleton.tsx';
import { ConversationActions } from './ConversationActions.tsx';
import { MessageBubble } from './MessageBubble.tsx';
import { MessageComposer } from './MessageComposer.tsx';
import { OperatorLockBanner } from './OperatorLockBanner.tsx';
import { StateBadge } from './StateBadge.tsx';
import type { ConversationActionHandlers } from './useConversationActions.ts';

interface Props {
  conv: ConversationDetail | null;
  hasSelection: boolean;
  error: string | null;
  adminId: string;
  actions: ConversationActionHandlers;
  className?: string;
  onRetry: () => void;
  onBack: () => void;
}

/** Panel central: header + hilo + composer. La lista de la izquierda nunca se desmonta. */
export function ChatPanel({
  conv,
  hasSelection,
  error,
  adminId,
  actions,
  className = '',
  onRetry,
  onBack,
}: Props) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: el scroll debe dispararse exactamente cuando cambia la cantidad de mensajes o la conversación
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [conv?.messages.length, conv?.conversationId]);

  const wrapper = `flex-col min-w-0 min-h-0 bg-white dark:bg-neutral-900 ${className}`;

  if (!hasSelection) {
    return (
      <section aria-label="Conversación" className={`${wrapper} items-center justify-center`}>
        <EmptyState message="Elegí una conversación de la lista para ver el hilo." />
      </section>
    );
  }

  if (error) {
    return (
      <section aria-label="Conversación" className={`${wrapper} items-center justify-center gap-3`}>
        <p className="text-sm text-danger-600 font-semibold text-center px-6">
          No se pudo cargar la conversación: {error}
        </p>
        <button type="button" onClick={onRetry} className="btn btn-primary btn-sm">
          Reintentar
        </button>
      </section>
    );
  }

  if (!conv) {
    return (
      <section aria-label="Conversación" className={wrapper}>
        <div className="p-6">
          <Skeleton rows={8} />
        </div>
      </section>
    );
  }

  const isOwnAdmin = conv.assignedAdminId === adminId;
  const isLockedByOther = conv.state === 'human_active' && !isOwnAdmin && !!conv.assignedAdminId;
  const composerDisabled = conv.state !== 'human_active' || !isOwnAdmin;

  return (
    <section aria-label="Conversación" className={wrapper}>
      <header className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between gap-3 bg-neutral-50 dark:bg-neutral-800/60">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="min-[780px]:hidden text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-200 font-semibold shrink-0"
          >
            ← Lista
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-neutral-900 dark:text-neutral-100 truncate">
                {conv.userDisplayName || conv.userIdentifier}
              </h2>
              <StateBadge state={conv.state} />
            </div>
            <p className="text-caption text-neutral-500 dark:text-neutral-400 truncate">
              {conv.channel} · {conv.tenantId}
            </p>
          </div>
        </div>
        <ConversationActions
          state={conv.state}
          isOwnAdmin={isOwnAdmin}
          onTake={actions.onTake}
          onRelease={actions.onRelease}
          onResolve={actions.onResolve}
          onReturnToBot={actions.onReturnToBot}
        />
      </header>

      {isLockedByOther && conv.assignedAdminName && (
        <OperatorLockBanner adminName={conv.assignedAdminName} />
      )}

      <div
        className="flex-1 overflow-y-auto min-h-0 px-4 py-4"
        role="log"
        aria-live="polite"
        aria-label="Historial de mensajes"
      >
        {conv.messages.length === 0 ? (
          <div className="text-center text-neutral-400 dark:text-neutral-500 text-sm py-12">
            Aún no hay mensajes
          </div>
        ) : (
          // Orden defensivo por timestamp ASC: el backend ya devuelve cronológico,
          // pero los eventos WS pueden llegar desordenados.
          [...conv.messages]
            .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
            .map((m) => (
              <MessageBubble
                key={m.id || `${m.timestamp}-${m.role}`}
                message={m}
                isOwnAdmin={m.adminId === adminId}
                canal={conv.channel}
              />
            ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <MessageComposer
        onSend={actions.onSend}
        disabled={composerDisabled}
        disabledReason={composerDisabledReason(conv, isLockedByOther)}
      />
    </section>
  );
}

/**
 * El backend sólo acepta mensajes del admin asignado con la conversación en
 * `human_active` (guard `not_assigned`). El composer refleja esa misma regla.
 */
function composerDisabledReason(
  conv: ConversationDetail,
  isLockedByOther: boolean,
): string | undefined {
  if (conv.state === 'bot_active') {
    return 'El bot está atendiendo. Haz clic en "Intervenir" para responder tú.';
  }
  if (conv.state === 'pending_human') return 'Haz clic en "Tomar" para responder.';
  if (conv.state === 'resolved') return 'Esta conversación ya está resuelta.';
  if (isLockedByOther) return `${conv.assignedAdminName ?? 'Otro operador'} está atendiendo`;
  return undefined;
}
